import { Info } from 'lucide-react';
import type { DataSection } from '../../shared/api/contract.ts';
import type { DataSource } from '../../shared/domain/index.ts';
import type { DataKind } from '../../shared/freshness/index.ts';
import { formatAge, formatDayTime } from '../lib/format.ts';
import { FRESHNESS_LABELS, sectionAgeMs, sectionFreshness } from './freshness.ts';

const isExternal = (url: string) => url.startsWith('http');

/** Nom du fournisseur (lien) + licence (lien visible, exigé par CC BY / ODbL). */
export function SourceCredit({ source }: { source: DataSource }) {
  return (
    <>
      {isExternal(source.homepage) ? (
        <a href={source.homepage} rel="noopener noreferrer" target="_blank">
          {source.name}
        </a>
      ) : (
        source.name
      )}
      {source.license.url !== undefined && (
        <>
          {' · '}
          <a href={source.license.url} rel="noopener noreferrer license" target="_blank">
            {source.license.name}
          </a>
        </>
      )}
    </>
  );
}

/**
 * Pied de bloc : « Mis à jour il y a 2 min » près de la donnée, et le détail de provenance (fournisseur, licence,
 * horodatages, fraîcheur) dans un volet « Sources et fraîcheur » — la provenance reste à un clic, sans casser la
 * hiérarchie visuelle.
 */
export function SourceLine({ section, now, kind }: { section: DataSection<unknown>; now: Date; kind?: DataKind }) {
  if (section.sources.length === 0) return null;
  const age = sectionAgeMs(section, now);
  const updatedAt = section.meta.observedAt ?? section.meta.fetchedAt;
  const status = kind === undefined ? null : sectionFreshness(section, kind, now);
  return (
    <div className="card-footer">
      {age !== null && updatedAt !== null && (
        <time className="card-footer__updated" dateTime={updatedAt}>
          Mis à jour {formatAge(age)}
        </time>
      )}
      <details className="sources">
        <summary>
          <Info size={14} aria-hidden="true" />
          <span>Sources et fraîcheur</span>
        </summary>
        <dl className="sources__panel">
          <dt>Source{section.sources.length > 1 ? 's' : ''}</dt>
          <dd>
            <ul>
              {section.sources.map((source) => (
                <li key={source.id}>
                  <SourceCredit source={source} />
                </li>
              ))}
            </ul>
          </dd>
          {section.meta.observedAt !== null && (
            <>
              <dt>Donnée observée</dt>
              <dd>
                <time dateTime={section.meta.observedAt}>{formatDayTime(section.meta.observedAt)}</time>
              </dd>
            </>
          )}
          {section.meta.fetchedAt !== null && (
            <>
              <dt>Récupérée par Ermont Live</dt>
              <dd>
                <time dateTime={section.meta.fetchedAt}>{formatDayTime(section.meta.fetchedAt)}</time>
              </dd>
            </>
          )}
          {status !== null && (
            <>
              <dt>Fraîcheur</dt>
              <dd>
                {FRESHNESS_LABELS[status]}
                {age !== null && ` (${formatAge(age)})`}
              </dd>
            </>
          )}
        </dl>
      </details>
    </div>
  );
}
